/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';



/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';






import OpmsField from '../../components/OpmsForm/OpmsField';

import OpmsInput from '../../components/OpmsForm/OpmsInput';

import TextField from '../../components/OpmsForm/Fields/TextField';

import Select from '../../components/OpmsForm/Fields/Select';

import ToggleButtonGroup from '../../components/OpmsForm/Fields/ToggleButtonGroup';

import { InputAdornment, Paper } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import PatientBreadcrumbs from '../../components/Breadcrumbs/PatientBreadcrumbs';
import Checkbox from '../../components/OpmsForm/Fields/Checkbox';
import DatePicker from '../../components/OpmsForm/Fields/DatePicker';
import NumericField, { PERCENTAGE_NO_DECIMAL } from '../../components/OpmsForm/Fields/NumericField';
import OpmsConditional, { IOpmsContionParams } from '../../components/OpmsForm/OpmsConditional';
import OpmsFieldGroup from '../../components/OpmsForm/OpmsFieldGroup';
import OpmsFieldLabel from '../../components/OpmsForm/OpmsFieldLabel';
import SourceDocuments from '../../components/OpmsForm/SourceDocuments';
import TrialForm from '../../components/OpmsForm/TrialForm';
import LookupsContext, { GetFieldLookupItem } from '../../context/LookupsContext';
import { PatientContext } from '@ngt/online-patient-management-ui';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDiagnosticAndPathologyDataProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(0)
    },
    subHeading: {
        padding: theme.spacing(3)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const hasHistopathologyTypeRequiresSpecification = ({ lookups, formState }: IOpmsContionParams<Dtos.DiagnosticAndPathologyData>) => GetFieldLookupItem(lookups, 'HistopathologyTypeId', formState.values.histopathologyTypeId!)?.type === Dtos.LookupSpecificationType.RequiresSpecification;
const hasPdLAssayTypeRequiresSpecification = ({ lookups, formState }: IOpmsContionParams<Dtos.DiagnosticAndPathologyData>) => GetFieldLookupItem(lookups, 'PdLAssayTypeId', formState.values.pdLAssayTypeId!)?.type === Dtos.LookupSpecificationType.RequiresSpecification;
const doesNotHaveTumourInfiltratingLymphocytesNotDone = ({ formState }: IOpmsContionParams<Dtos.DiagnosticAndPathologyData>) => !formState.values.tumourInfiltratingLymphocytesNotDone;
const pdLTestingCompleted = ({ lookups, formState }: IOpmsContionParams<Dtos.DiagnosticAndPathologyData>) => GetFieldLookupItem(lookups, 'PdLTestingCompletedId', formState.values.pdLTestingCompletedId!)?.type === Dtos.LookupYesNoType.Yes;
/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const TnmClinicalStagingTumourIdField = () => {
    const [patient] = React.useContext(PatientContext);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;

    const lookups = React.useContext(LookupsContext);

    const tnmClinicalStagingTumourLookup = React.useMemo(() => {
        return lookups?.find(l => l.propertyName === 'TnmClinicalStagingTumourId') ?? null;
    }, [lookups]);

    const tnmClinicalStagingTumourLookupItemsToUse = React.useMemo(() => {
        return patientCohortC ? tnmClinicalStagingTumourLookup?.items?.filter(i => i.value !== 'T1') : tnmClinicalStagingTumourLookup?.items;
    }, [tnmClinicalStagingTumourLookup, patientCohortC]);

    const tnmClinicalStagingTumourLookupToUse = React.useMemo(() => {
        return {
            propertyName: 'TnmClinicalStagingTumourId',
            items: tnmClinicalStagingTumourLookupItemsToUse
        } as Dtos.Lookup
    }, [tnmClinicalStagingTumourLookupItemsToUse]);

    return <OpmsField name="TnmClinicalStagingTumourId">
        <OpmsInput
            component={Select}
            lookup={tnmClinicalStagingTumourLookupToUse}
        />
    </OpmsField>
}

const DiagnosticAndPathologyData: React.FunctionComponent<IDiagnosticAndPathologyDataProps> = () => {
    const classes = useStyles();

    const [patient] = React.useContext(PatientContext);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;

    return (
        <PageLayout
            breadcrumbs={
                <PatientBreadcrumbs />
            }
            heading="Diagnostic and Pathology Data"
        >
            <Paper
                className={classes.container}
            >
                <TrialForm
                    formClass={Dtos.DiagnosticAndPathologyData}
                >
                    <OpmsField name="HistopathologicDiagnosisDate">
                        <OpmsInput
                            component={DatePicker}
                        />
                    </OpmsField>
                    <OpmsField name="PrimaryTumourSiteId">
                        <OpmsInput
                            component={Select}
                        />
                    </OpmsField>
                    {
                        <TnmClinicalStagingTumourIdField/>
                    }
                    <OpmsField name="TnmClinicalStagingNodesId">
                        <OpmsInput
                            component={Select}
                        />
                    </OpmsField>
                    <OpmsField name="TnmClinicalStagingDistantMetastasisId">
                        <OpmsInput
                            component={Select}
                        />
                    </OpmsField>
                    <OpmsFieldGroup>
                        <OpmsField
                            name="HistopathologyTypeId"
                            instruction={
                                patientCohortC ?
                                    <small>
                                        <br />
                                        <i>Note: If histopathology reports cancer as "invasive carcinoma NOS/NST", please enter as "infiltrating ductal carcinoma".</i>
                                    </small>
                                    :
                                    undefined
                            }
                        >
                            <OpmsInput
                                component={Select}
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticAndPathologyData}
                            condition={hasHistopathologyTypeRequiresSpecification}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField name="HistopathologyTypeSpecification">
                                <OpmsInput
                                    component={TextField}
                                />
                            </OpmsField>
                        </OpmsConditional>
                        {
                            !!patientCohortC && <OpmsField
                                name="HistopathologicalGradeId"
                            >
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                        }
                    </OpmsFieldGroup>
                    <OpmsField name="ConfirmClinicalStageDiagnosisId">
                        <OpmsInput
                            component={Select}
                        />
                    </OpmsField>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Receptor Status
                        </Typography>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            Oestrogen Receptor
                        </Typography>
                        <OpmsField name="OestrogenReceptorPercentageRangeId">
                            <OpmsInput
                                component={Select}
                            />
                        </OpmsField>
                        <OpmsField
                            name="OestrogenReceptorPercentageValue"
                            instruction={
                                patientCohortC ?
                                    <small>
                                        <br />
                                        <i>Note: If result reported as "NEGATIVE" or "POSITIVE" rather than %, please confirm pathology lab ranges and enter in confirmed percentage.</i>
                                    </small>
                                    :
                                    undefined
                            }
                        >
                            <OpmsInput
                                component={NumericField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            %&nbsp;
                                        </InputAdornment>
                                    )
                                }}
                                numberFormat={PERCENTAGE_NO_DECIMAL}
                            />
                        </OpmsField>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            Progesterone Receptor
                        </Typography>
                        <OpmsField name="ProgesteroneReceptorPercentageRangeId">
                            <OpmsInput
                                component={Select}
                            />
                        </OpmsField>
                        <OpmsField
                            name="ProgesteroneReceptorPercentageValue"
                            instruction={
                                patientCohortC ?
                                    <small>
                                        <br />
                                        <i>Note: If result reported as "NEGATIVE" or "POSITIVE" rather than %, please confirm pathology lab ranges and enter in confirmed percentage.</i>
                                    </small>
                                    :
                                    undefined
                            }
                        >
                            <OpmsInput
                                component={NumericField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            %&nbsp;
                                        </InputAdornment>
                                    )
                                }}
                                numberFormat={PERCENTAGE_NO_DECIMAL}
                            />
                        </OpmsField>
                        <Typography
                            variant="h3"
                            color="secondary"
                            className={classes.subHeading}
                        >
                            HER Expression
                        </Typography>
                        <OpmsField name="Her2ExpressionIhcScoreId">
                            <OpmsInput
                                component={Select}
                            />
                        </OpmsField>
                        <OpmsField name="Her2ExpressionIshScoreId">
                            <OpmsInput
                                component={Select}
                            />
                        </OpmsField>
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Tumour Infiltrating Lymphocytes (TIL)
                        </Typography>
                        <OpmsField
                            name="TumourInfiltratingLymphocytesNotDone"
                            label={<OpmsFieldLabel name="TumourInfiltratingLymphocytesValue" />}
                            instruction={
                                patientCohortC ?
                                    <small>
                                        <br />
                                        <i>Note: If TILs reported as a range (i.e., 0-30%), report the highest value (i.e., =30%).</i>
                                    </small>
                                    :
                                    undefined
                            }
                        >
                            <OpmsInput
                                component={Checkbox}
                                label="Not Done"
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticAndPathologyData}
                            condition={doesNotHaveTumourInfiltratingLymphocytesNotDone}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField
                                name="TumourInfiltratingLymphocytesRangeId"
                                label={null}
                            >
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsField
                                name="TumourInfiltratingLymphocytesValue"
                                label={null}
                            >
                                <OpmsInput
                                    component={NumericField}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                %&nbsp;
                                            </InputAdornment>
                                        )
                                    }}
                                    numberFormat={PERCENTAGE_NO_DECIMAL}
                                />
                            </OpmsField>
                        </OpmsConditional>
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            Programmed Death-Ligand 1 (PDL-1)
                        </Typography>
                        <OpmsField name="PdLTestingCompletedId">
                            <OpmsInput
                                component={ToggleButtonGroup}
                            />
                        </OpmsField>
                        <OpmsConditional
                            formClass={Dtos.DiagnosticAndPathologyData}
                            condition={pdLTestingCompleted}
                            mode="Enable"
                            subscription={{ values: true }}
                        >
                            <OpmsField name="PdLAssayTypeId">
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsConditional
                                formClass={Dtos.DiagnosticAndPathologyData}
                                condition={hasPdLAssayTypeRequiresSpecification}
                                mode="Enable"
                                subscription={{ values: true }}
                            >
                                <OpmsField name="PdLAssayTypeSpecification">
                                    <OpmsInput
                                        component={TextField}
                                    />
                                </OpmsField>
                            </OpmsConditional>
                            <OpmsField name="PdLResultsId">
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsField
                                name="PdLValue"
                                label={null}
                            >
                                <OpmsInput
                                    component={NumericField}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                %&nbsp;
                                            </InputAdornment>
                                        )
                                    }}
                                    numberFormat={PERCENTAGE_NO_DECIMAL}
                                />
                            </OpmsField>
                        </OpmsConditional>
                    </OpmsFieldGroup>
                    <OpmsFieldGroup>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.subHeading}
                        >
                            BRCA Germline Status
                        </Typography>
                        <OpmsField name="BrcaGermlineStatusId">
                            <OpmsInput
                                component={Select}
                            />
                        </OpmsField>
                    </OpmsFieldGroup>
                    <SourceDocuments />
                </TrialForm>
            </Paper>
        </PageLayout>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default DiagnosticAndPathologyData;